// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import type { Resource, FetchResponse } from '@gandi/resource/redux';

import { createLogger } from 'gandi.redux-logger';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { FetchProps } from '../../types';

import {
  useResourceListStatuses,
  type ResourceStatuses,
  useResourceOneStatuses,
} from './useResourceStatuses';

export type { ResourceStatuses };

type UseListResourceOption = {
  params?: $ReadOnly<FetchProps>,
  actionExtraParams?: { ... },
  enabled?: boolean,
};

const emptyListOptions: UseListResourceOption = {};

/**
 * Load list resource and get state of the resource
 *
 * You can lazy load data by toggling `enabled` option. e.g.
 * `useListResource(resource, url, label, { enabled: false })`
 * it will only fetch if `enabled` is `true` or `undefined`.
 */
export const useListResource = (
  resource: Resource<>,
  url: string,
  label: string,
  {
    params,
    actionExtraParams,
    enabled = true,
  }: UseListResourceOption = emptyListOptions,
): ResourceStatuses => {
  const logger = createLogger(resource.name);
  const dispatch = useDispatch();

  const resourceState = useResourceListStatuses(resource, label);

  React.useEffect(() => {
    async function fetcher() {
      try {
        await dispatch(
          // $FlowFixMe[cannot-spread-inexact] We want to allow anything
          resource.fetch(url, {
            label,
            params,
            on20X: (response: FetchResponse<>) => response,
            not20X: (response: FetchResponse<>) => Promise.reject(response),
            ...actionExtraParams,
          }),
        );
      } catch (response) {
        const fields = {
          res: response,
          statusCode: response.status,
          params: { ...params },
        };

        switch (response.status) {
          case 403:
            dispatch(
              logger.warn(fields, `No permissions to fetch ${resource.name}`),
            );
            break;
          case 500:
            dispatch(logger.fatal(fields, `Cannot fetch ${resource.name}`));
            break;
          default:
            dispatch(logger.info(fields, `Cannot fetch ${resource.name}`));
        }
      }
    }
    if (enabled && resourceState.shouldFetch) {
      fetcher();
    }
  }, [
    label,
    resourceState.shouldFetch,
    enabled,
    params,
    resource,
    url,
    dispatch,
    logger,
    actionExtraParams,
  ]);

  return resourceState;
};

type UseResourceOptions = {
  params?: $ReadOnly<FetchProps>,
  actionExtraParams?: { ... },
  enabled?: boolean,
};

const emptyOptions: UseResourceOptions = {};

/**
 * Load one resource and get state of the resource
 *
 * You can lazy load data by toggling `enabled` option. e.g.
 * `useResource(resource, url, label, { enabled: false })`
 * it will only fetch if `enabled` is `true` or `undefined`.
 */
export const useResource = (
  resource: Resource<>,
  url: string,
  label: string,
  {
    params,
    actionExtraParams,
    enabled = true,
  }: UseResourceOptions = emptyOptions,
): ResourceStatuses => {
  const logger = createLogger(resource.name);

  const dispatch = useDispatch();

  const resourceState = useResourceOneStatuses(resource, label);

  const shouldFetch = useAppSelector(state =>
    resource.selectShouldFetchOne(state, label),
  );

  React.useEffect(() => {
    async function fetcher() {
      try {
        await dispatch(
          // $FlowFixMe[cannot-spread-inexact] We want to allow anything
          resource.fetchOne(url, {
            label,
            params,
            on20X: (response: FetchResponse<>) => response,
            not20X: (response: FetchResponse<>) => Promise.reject(response),
            ...actionExtraParams,
          }),
        );
      } catch (response) {
        const fields = {
          res: response,
          statusCode: response.status,
          params: { ...params },
        };

        switch (response.status) {
          case 403:
            dispatch(
              logger.warn(fields, `No permissions to fetch ${resource.name}`),
            );
            break;
          case 500:
            dispatch(logger.fatal(fields, `Cannot fetch ${resource.name}`));
            break;
          default:
            dispatch(logger.info(fields, `Cannot fetch ${resource.name}`));
        }
      }
    }
    if (enabled && shouldFetch) {
      fetcher();
    }
  }, [
    dispatch,
    label,
    shouldFetch,
    enabled,
    params,
    resource,
    url,
    actionExtraParams,
    logger,
  ]);

  return resourceState;
};
